<template>
  <div class="privacy-page">
    <h1 class="page-header">{{appName}} User Privacy Agreement</h1>
    <div class="main-content">
      <h2 class="doc-title">{{appName}} Personal Data Privacy Notice</h2>

      <p>
        When you use our mobile application services, we may collect and process your personal information.
        This notice elaborates on how our platform collects, uses and discloses your data during your use of related services.
      </p>
      <br>
      <p class="warm-tip">
        To fully understand the protection rules of your personal information, please read this privacy notice carefully.
      </p>
      <br>

      <p class="catalog-title">The main contents of this notice are as follows:</p>
      <p>1. Collection and application of your personal data</p>
      <p>2. Sharing, transfer and public disclosure of personal data</p>
      <p>3. Application of Cookies, SDK and other auxiliary technologies</p>
      <p>4. Security protection measures for personal data</p>
      <p>5. Your rights to manage personal data</p>
      <p>6. Processing rules for information of minor users</p>
      <p>7. Storage specifications of personal data</p>
      <p>8. Revision and notification mechanism of the agreement</p>
      <p>9. Contact method for consultation</p>
      <br>

      <h3 class="chapter-title">Preface</h3>
      <p>
        {{appName}} always takes user information security as a priority. We strictly keep all collected user data confidential
        and build a complete information protection system to provide safe and reliable services for every user.
      </p>
      <br>

      <h3 class="chapter-title">1. Collection and Application of Personal Data</h3>
      <p>
        Personal data refers to all information recorded by electronic or other means that can identify a specific individual
        or reflect personal activities, including name, birth date, biometric features, contact address, communication records,
        account passwords, property data, physical health status, transaction details and other related content.
      </p>
      <br>
      <p>
        Sensitive personal data refers to information that may bring risks to personal and property safety, damage personal reputation
        or lead to unfair treatment once leaked, illegally transmitted or abused. It covers biometric data, bank account information,
        chat content, asset status, real-time location, health records, transaction data and information of users under 14 years old.
      </p>
      <br>

      <p class="strong-text">You hereby acknowledge and agree to the following terms:</p>
      <p>
        1. We launch various products and services to meet your diverse needs. Different service modules will collect different types
        and ranges of personal data, and the specific collection rules shall be subject to the actual functions you use.
      </p>
      <p>
        2. We continuously upgrade products and technologies to optimize user experience. When launching new functions or optimizing existing services,
        we may need to collect new data or adjust the way of using existing information. We will inform you through notice updates,
        pop-up reminders or page prompts, and we will only collect relevant data after obtaining your explicit consent.
        You can contact us at any time if you have questions or suggestions.
      </p>
      <p>
        3. The data obtained from a single function can be used in other services of our platform, such as pushing customized content,
        sending security reminders and providing personalized recommendations. If we use your data beyond the agreed scope at the time of collection,
        we will notify you again and obtain your approval in advance.
      </p>
      <br>

      <p class="notice-label">Important Reminder:</p>
      <p>
        Our platform provides user matching and recommendation services. The nickname, age, gender, zodiac sign, occupation, workplace,
        native place, frequent activity locations, personal signature, avatar, photos and dynamic content you publish
        may be displayed and recommended to other users.
      </p>
      <br>

      <h3 class="chapter-title">Business Functions and Collected Information</h3>
      <p>
        In accordance with relevant national industry specifications, {{appName}} is categorized as a social communication application.
        Its core service is to provide online instant messaging including text, pictures, voice and video.
        The required personal data collected by each function is specified below:
      </p>
      <br>
      <p>
        1. Account Registration: To complete account registration, you need to provide your email address, nickname, gender,
        country, birth date and avatar. These information are used to create your account and activate social functions.
        Meanwhile, we will collect device-related information such as device model, system version, device unique identifier,
        network status, application list and sensor data to ensure the stable operation of software and services.
      </p>
      <p>
        2. Chat Function: Using basic chat services will not require additional information submission. We will collect your account data
        to maintain normal service operation and account security. When sending pictures, voice messages or applying for video calls,
        the application will request access to your album and microphone. You can reject these permissions, which will not affect basic chat services.
      </p>
      <p>
        3. Video Call: This function requires you to authorize camera and microphone access. Without relevant permissions,
        you cannot use the video call service normally.
      </p>
      <p>
        4. Browse Moments: Viewing content in the moments section does not require any personal data or additional permissions.
      </p>
      <p>
        5. Visit User Homepage: You can view other users' photos and videos on their homepage without extra permissions.
        If you initiate a video call on the homepage, you need to enable camera and microphone permissions.
      </p>
      <p>
        6. Personal Information Editing: When replacing your avatar, the app will request album access permission.
        Refusing this permission will not hinder the use of basic functions.
      </p>
      <p>
        7. Payment Service: The payment function is provided by our cooperative third-party payment institutions.
        We will not obtain your payment-related information. Refusing to authorize relevant permissions will only disable the payment function,
        and other basic services can be used normally.
      </p>
      <p>
        8. Message Reminder: We will apply for automatic startup permission to push message reminders in a timely manner.
        You can turn off this permission in your mobile phone system settings at any time.
      </p>
      <br>

      <p>
        Permissions such as camera, album and microphone are not enabled by default. When you use related functions,
        we will pop up a separate window to ask for your authorization. You can freely turn on or off various permissions in device settings.
        Closing permissions means you withdraw authorization, and we will stop collecting corresponding data and suspend related services.
        Closing permissions will not affect the data collected with your previous authorization.
      </p>
      <br>

      <p>
        We may send service notifications, security verification messages and user experience research content to your reserved email.
        If you publish other people's personal information on the platform, you must ensure that you have obtained legal authorization in advance.
      </p>
      <br>

      <p>
        Your submitted information will be used during your use of services. After you cancel your account or stop using certain functions,
        we will stop using your data and complete anonymization or deletion in accordance with legal regulations.
      </p>
      <br>

      <h3 class="chapter-title">Rules for Using Collected Data</h3>
      <p>
        We will only use your data for the purposes stated in this notice. If we need to use data for other purposes,
        we will obtain your prior consent. When we obtain data from third parties, we will require them to collect information
        in compliance with laws and obtain user consent. We will verify the legality of data sources and adopt the same strict
        security protection measures for externally obtained data.
      </p>
      <br>

      <p class="strong-text">Exceptions to Authorization Requirements</p>
      <p>
        In the following situations, we do not need your consent to collect and use personal data,
        and we may not respond to your requests for modification, deletion or withdrawal of consent:
      </p>
      <p>1. Fulfill the statutory obligations stipulated by laws and regulations</p>
      <p>2. Involve national security and national defense security</p>
      <p>3. Involve public safety, public health and social public interests</p>
      <p>4. For criminal investigation, litigation, trial and judgment execution</p>
      <p>5. To protect the legitimate rights and interests of individuals when it is difficult to obtain consent</p>
      <p>6. The information is voluntarily disclosed by yourself</p>
      <p>7. Collect data from legally public information channels</p>
      <br>

      <p>
        We will conduct desensitization processing, statistical analysis and algorithm training on collected data to optimize products and services.
        The processed anonymous data cannot identify specific individuals, so no additional notification or consent is required.
        If we stop operating the platform, we will immediately stop data collection, issue service termination announcements,
        and delete or anonymize all stored personal data.
      </p>
      <br>

      <h3 class="chapter-title">Information You Publish</h3>
      <p>
        You can share text, pictures and other content through our platform, which may contain your personal or sensitive information.
        Please carefully consider before publishing private content. You can delete the content you have published at any time,
        but please note that these content may be saved by other users or third parties beyond our management.
      </p>
      <br>

      <h3 class="chapter-title">2. Sharing, Transfer and Public Disclosure of Personal Data</h3>
      <h4 class="sub-chapter">Data Sharing</h4>
      <p>
        We will not share your personal data with external enterprises, organizations or individuals, except for the following circumstances:
      </p>
      <p>1. Share data after obtaining your explicit authorization;</p>
      <p>2. Share data as required by laws, regulations or judicial authorities;</p>
      <p>3. Share necessary data with affiliated companies to provide joint services and protect user security.
        We will apply for your re-authorization if sensitive data is involved or the usage purpose is changed;</p>
      <p>4. Provide necessary data to authorized service partners, who can only use the data to complete entrusted work.</p>
      <br>

      <h4 class="sub-chapter">Data Transfer</h4>
      <p>
        We will not transfer your personal data to any external party unless:
      </p>
      <p>1. You have given explicit consent to the data transfer;</p>
      <p>2. In case of merger, acquisition or bankruptcy reorganization, the new holder of data must abide by this agreement.
        Otherwise, they need to obtain your re-authorization.</p>
      <br>

      <h4 class="sub-chapter">Public Disclosure</h4>
      <p>1. Disclose information with your explicit consent or active operation;</p>
      <p>2. Disclose relevant information in accordance with laws and platform rules to safeguard public interests
        when you violate relevant regulations.</p>
      <br>

      <h3 class="chapter-title">3. Use of Cookies and SDK Technologies</h3>
      <h4 class="sub-chapter">Cookies Technology</h4>
      <p>
        We will store small data files called Cookies on your device to ensure the normal operation of services,
        optimize your use experience and push content you may be interested in. You can clear Cookies through browser settings,
        but some functions relying on Cookies may become unavailable afterwards.
      </p>
      <br>

      <h4 class="sub-chapter">Third-Party SDK</h4>
      <p>
        Some functions of the application rely on third-party SDK services. Third-party service providers will independently apply for
        permissions and collect data. The main integrated SDKs include: Google Firebase Push SDK, Third-party Payment SDK,
        NetEase Yunxin SDK, Shengwang SDK, AppsFlyer SDK, Facebook SDK, Huawei Push SDK.
        Rejecting relevant permissions will only make the corresponding third-party functions unavailable,
        and will not affect the normal use of other functions of {{appName}}.
      </p>
      <br>

      <h3 class="chapter-title">4. Protection of Personal Data Security</h3>
      <p>
        We abide by relevant laws and normative documents, and adopt multiple measures to protect your information security:
      </p>
      <p>
        1. We use encryption, data desensitization, access control and other technical means to prevent unauthorized access,
        leakage, damage and loss of personal data. We also carry out regular training for employees to enhance data protection awareness.
      </p>
      <p>
        2. We have established a complete full-cycle data security management system. Once a data security accident occurs,
        we will start the emergency plan immediately and inform users through announcements or message pushes.
      </p>
      <p>
        3. We only collect necessary information and retain your data within the time limit required by service purposes,
        unless the retention period is extended in accordance with laws.
      </p>
      <p>
        4. The network environment cannot be absolutely secure. Please do not send sensitive personal information through unofficial channels.
        If you find your account or password is leaked, please contact our customer service in time.
      </p>
      <br>

      <h3 class="chapter-title">5. Manage Your Personal Data</h3>
      <h4 class="sub-chapter">Query Personal Data</h4>
      <p>
        Within the scope permitted by law, you have the right to view your personal data.
        You can view and modify information via <strong>[Mine - Upper Right Corner - Settings]</strong>.
        Click the avatar in the personal center to check and edit nickname, avatar, birth date and personal introduction.
      </p>
      <br>

      <h4 class="sub-chapter">Correct Personal Data</h4>
      <p>
        If you find errors in your personal data, you can submit a correction application through the above entry.
      </p>
      <br>

      <h4 class="sub-chapter">Delete Personal Data</h4>
      <p>
        You can delete part of your personal data independently. You can also apply for data deletion in the following situations:
      </p>
      <p>1. Our data processing acts violate relevant laws and regulations;</p>
      <p>2. We collect and use your data without your explicit consent;</p>
      <p>3. We seriously violate the agreed terms;</p>
      <p>4. You stop using services or cancel your account;</p>
      <p>5. We permanently stop providing services for you.</p>
      <br>

      <h4 class="sub-chapter">Adjust Authorization Scope</h4>
      <p>
        For additional data collection permissions, you can enable or cancel authorization by yourself,
        or contact our customer service for assistance.
      </p>
      <br>

      <h3 class="chapter-title">6. Processing of Minors' Information</h3>
      <p class="forbid-rule">This application forbids users under 18 years of age to register and use.</p>
      <p>
        Our services are not oriented to users under 18 years old. We will not actively collect or trade minors' personal information.
        If we accidentally obtain data of minor users, we will delete it immediately.
        If we adjust relevant rules in the future, we will obtain verifiable consent from guardians before collecting minors' information.
      </p>
      <br>

      <h3 class="chapter-title">7. Storage of Personal Data</h3>
      <p>
        Our service servers are deployed in many regions around the world. Your personal data may be stored and used on servers outside your region.
        You agree to the cross-regional transmission and storage of your data for the purposes specified in this notice.
      </p>
      <br>

      <h3 class="chapter-title">8. Agreement Revision and Notification</h3>
      <p>
        We have the right to revise this privacy notice. You can check the latest version through the link on our official website at any time.
        We will release official announcements when major content changes occur. Your continued use of our services after the revision
        means you accept the new agreement. If you do not agree with the revised terms, please stop using our services.
      </p>
      <br>

      <h3 class="chapter-title">9. Contact Us</h3>
      <p>
        If you have any questions about this privacy notice or data protection related matters,
        please send an email to: <span class="mail-text">{{appName}}@{{appName}}.site</span>
      </p>
    </div>
  </div>
</template>

<script>
export default {
  data() {
    return {};
  },
  created() {}
};
</script>

<style scoped>
/* 全新独立样式，和前两版完全区分 */
.privacy-page {
  width: 100%;
  padding: 20px 12px;
  box-sizing: border-box;
  background-color: #f0f2f5;
  min-height: 100vh;
  font-family: "Microsoft Yahei", sans-serif;
}

.page-header {
  font-size: 26px;
  color: #111827;
  text-align: center;
  margin: 0 0 24px 0;
  font-weight: bold;
}

.main-content {
  max-width: 850px;
  margin: 0 auto;
  background: #ffffff;
  padding: 32px;
  border-radius: 12px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.06);
  font-size: 14px;
  line-height: 1.8;
  color: #4b5563;
}

.doc-title {
  font-size: 20px;
  color: #0369a1;
  text-align: center;
  margin: 0 0 18px 0;
  font-weight: 600;
}

.warm-tip {
  background-color: #f0fdf4;
  color: #047857;
  padding: 10px 16px;
  border-radius: 6px;
  font-weight: 500;
}

.catalog-title {
  font-size: 16px;
  color: #1f2937;
  font-weight: 600;
  margin: 10px 0 8px 0;
}

.chapter-title {
  font-size: 18px;
  color: #0284c7;
  margin: 28px 0 12px 0;
  font-weight: 600;
  border-left: 4px solid #0284c7;
  padding-left: 10px;
}

.sub-chapter {
  font-size: 15px;
  color: #374151;
  font-weight: 600;
  margin: 14px 0 6px 0;
}

.strong-text {
  font-weight: 600;
  color: #c2410c;
}

.notice-label {
  font-weight: 600;
  color: #c2410c;
}

.forbid-rule {
  font-size: 15px;
  font-weight: 600;
  color: #dc2626;
}

.mail-text {
  color: #0369a1;
  font-weight: 500;
}
</style>